import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { exportView } from "../../navigation";
import { Flags, SegFlags, translate } from "../../common/providers";
import { Resizer } from "../../common/components";
import styles from "./Chat.module.css";
import { ChatList, CurrentConversation, Search } from "./components";
import { useApi, useTrackEvent } from "../../common/hooks";
import { AiChatService, connectSSE } from "../../api";

const Chat = () => {
	const trackEvent = useTrackEvent();
	const { call: askNewQuestion } = useApi(AiChatService.askNewQuestion);
	const { call: getChat } = useApi(AiChatService.getChat);
	const [chatPayload, setChatPayload] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedChat, setSelectedChat] = useState(0);
	const [toRefresh, setToRefresh] = useState(true);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const projectId = useSelector(({ context }) => context.project?.id);
	const sseConnectionRef = useRef(null);
	const handleAutomationMessage = ({ reason, chatId }) => {
		if (reason === "COMPLETED") {
			setChatPayload("");
			setToRefresh(true);
			setSelectedChat(parseInt(chatId, 10));
		}
		if (reason === "ERROR") {
			console.error("Failed to generate");
		}
		setLoading(false);
	};
	useEffect(() => {
		const documentTitle = translate("ai-chat.title");
		document.title = documentTitle;
		trackEvent("Chat_visit");
		// eslint-disable-next-line
	}, []);
	useEffect(() => {
		if (!projectId) {
			return null;
		}
		sseConnectionRef.current?.();
		const onMessage = (message) => {
			const { data } = message;
			handleAutomationMessage({ reason: data.action, chatId: data.payload.chatId });
		};
		const onError = (error) => {
			console.error(error);
		};
		sseConnectionRef.current = connectSSE(`projectchat/projects/${projectId}/chats/subscribe`, {
			onMessage,
			onError,
		});
		return () => {
			sseConnectionRef.current?.();
		};
	}, [projectId]);
	const handleChangeChatPayload = (value) => {
		setChatPayload(value);
	};
	const handleAskNewQuestion = () => {
		askNewQuestion({ projectId }, { value: chatPayload })
			.then(() => {
				setLoading(true);
				trackEvent("Chat_send_message");
			})
			.catch(console.error);
	};
	const handleChangeSelectedChat = (chatId) => {
		setChatPayload("");
		setSelectedChat(chatId);
	};
	const handleResponse = () => {
		setToRefresh(false);
	};
	return (
		<>
			<div className={styles.mainDisplay} data-caution-banner-disabled={cautionBannerDisabled}>
				<div className={styles.sideDrawer__main}>
					<ChatList
						loading={loading}
						selectedChat={selectedChat}
						toRefresh={toRefresh}
						onChangeSelectedChat={handleChangeSelectedChat}
						onResponse={handleResponse}
						onSetToRefresh={setToRefresh}
					/>
				</div>
				<Resizer active className={styles.resizer} max={30} min={15} />
				<div className={styles.siderDrawer__content}>
					<CurrentConversation
						chatPayload={chatPayload}
						loading={loading}
						selectedChat={selectedChat}
						onChangeTextFilter={handleChangeChatPayload}
						onGetChat={getChat}
					/>
					<Search
						chatPayload={chatPayload}
						loading={loading}
						selectedChat={selectedChat}
						onAskQuestion={handleAskNewQuestion}
						onChangeChatPayload={handleChangeChatPayload}
					/>
				</div>
			</div>
		</>
	);
};

export default exportView({
	path: "/projects/:projectId/chat",
	localesPath: "/chat/locales",
	component: Chat,
	flag: Flags.PROJECT_CHAT,
	segFlag: SegFlags.CHAT_PROJECT,
});
